/**
 * ScamBlocker AI — Raporlar (/reports)
 * 7 günlük çağrı trendi (grafik) + 30 günlük CSV dışa aktarım.
 */

import { useQuery } from '@tanstack/react-query';
import { BarChart3, Download } from 'lucide-react';
import { useState } from 'react';
import styles from '@/features/_shared/page.module.css';
import apiClient from '@/shared/lib/api/client';
import { API } from '@/shared/lib/api/endpoints';

interface WeeklyDay {
  day: string;
  calls: number;
  avg_risk: number | null;
  critical: number;
}

export function ReportsPage() {
  const [exporting, setExporting] = useState(false);

  const { data, isLoading, isError } = useQuery<{ daily: WeeklyDay[] }>({
    queryKey: ['reports-weekly'],
    queryFn: async () => (await apiClient.get(API.org.reportWeekly)).data,
  });

  const days = data?.daily ?? [];
  const maxCalls = Math.max(1, ...days.map((d) => d.calls));

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await apiClient.get(API.org.reportExport, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'scamblocker-calls.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      window.alert('Dışa aktarım başarısız.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <BarChart3 size={22} className={styles.titleIcon} />
        <h1 className={styles.title}>Raporlar</h1>
        <span className={styles.spacer} />
        <button type="button" className={styles.actionBtn} disabled={exporting} onClick={exportCsv}>
          <Download size={14} /> {exporting ? 'Hazırlanıyor…' : 'CSV İndir (30 gün)'}
        </button>
      </div>

      <div className={styles.tableWrapper} style={{ padding: '1rem 1.25rem' }}>
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem', color: 'var(--color-text)' }}>
          Günlük Çağrı Hacmi (7 gün)
        </h3>
        {isLoading && (
          <div className={styles.center}>
            <span className={styles.spinner} />
          </div>
        )}
        {isError && <div className={styles.errorBox}>Rapor yüklenemedi.</div>}
        {!isLoading && !isError && days.length === 0 && (
          <div className={styles.empty}>Son 7 günde veri yok.</div>
        )}
        {days.length > 0 && (
          <div className={styles.chart}>
            {days.map((d) => {
              const h = Math.round((d.calls / maxCalls) * 100);
              const hot = d.critical > 0;
              return (
                <div
                  key={d.day}
                  className={styles.barCol}
                  title={`${d.calls} çağrı · ${d.critical} kritik · ort. risk ${d.avg_risk ?? '—'}`}
                >
                  <div className={styles.barValue}>{d.calls}</div>
                  <div
                    className={styles.bar}
                    style={{
                      height: `${Math.max(4, h)}%`,
                      background: hot ? 'var(--color-danger)' : 'var(--color-primary)',
                    }}
                  />
                  <div className={styles.barLabel}>
                    {new Date(d.day).toLocaleDateString('tr-TR', {
                      day: '2-digit',
                      month: '2-digit',
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
